import {
    DeliveryDestinationType,
    DeliveryState,
    DeliveryType,
    GetTourPlanningForDateQuery,
    MicrostoreColumnTemplateType,
    TaskState,
    TaskType,
    TourState,
    TourStopInput,
    TourStopType,
} from 'data/__generated__';
import { getDeliveryNoteTotemLabel } from 'helpers/totemLabel';
import { BUNDLE_TYPES_LABELS } from 'pages/Bundles/constants/types';
import { SITE_CONTRACT_TYPES_OPTIONS } from 'pages/Sites/constants/contract-types';

export type Tour = GetTourPlanningForDateQuery['tourPlanningForDate']['tours'][number];
export type TourDelivery = Tour['deliveries'][number];
export type TourTask = GetTourPlanningForDateQuery['tourPlanningForDate']['unassignedTasks'][number];

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
    [TaskType.Audit]: 'Contrôle site',
    [TaskType.SpecificWork]: 'Travaux spécifiques',
    [TaskType.BoxPickup]: 'Ramassage de caisses vides',
    [TaskType.Ticket]: 'Ticket',
    [TaskType.Other]: 'Autre',
};

export const TASK_STATE_LABELS: Record<TaskState, string> = {
    [TaskState.Created]: 'Créée',
    [TaskState.InProgress]: 'En cours',
    [TaskState.Finished]: 'Terminée',
    [TaskState.Failed]: 'Échouée',
    [TaskState.Aborted]: 'Annulée',
};

export const TASK_STATES_CLOSED = [TaskState.Finished, TaskState.Failed, TaskState.Aborted];

export const taskLabel = (task: TourTask): string =>
    `${task.site?.name ?? 'Site inconnu'} · ${TASK_TYPE_LABELS[task.type]}`;

export type TourStopItem = TourDelivery | TourTask;

export const isTask = (stop: TourStopItem): stop is TourTask => stop.__typename === 'Task';

/** « 3 livraisons · 1 tâche » — the task part only when there is one. */
export const stopCountLabel = (deliveryCount: number, taskCount: number): string =>
    [
        `${deliveryCount} livraison${deliveryCount > 1 ? 's' : ''}`,
        taskCount ? `${taskCount} tâche${taskCount > 1 ? 's' : ''}` : null,
    ]
        .filter(Boolean)
        .join(' · ');

export const orderedStops = (tour: Pick<Tour, 'stops' | 'deliveries'>): TourStopItem[] => {
    const deliveryById = new Map(tour.deliveries.map((delivery) => [delivery._id, delivery]));
    return tour.stops.flatMap((stop): TourStopItem[] => {
        if (stop.__typename === 'Task') return [stop];
        const delivery = deliveryById.get(stop._id);
        return delivery ? [delivery] : [];
    });
};

export const stopsOf = (tour: Pick<Tour, 'stops'>): TourStopInput[] =>
    tour.stops.map((stop) => ({
        type: stop.__typename === 'Task' ? TourStopType.Task : TourStopType.Delivery,
        stopId: stop._id,
    }));

/**
 * Display label of a delivery, everywhere (chips, cards, toasts, hovers). A freefood delivery has no site and
 * its totem is often unnamed — the organization is then what the dispatcher actually recognises.
 */
export const deliveryLabel = (delivery: TourDelivery): string =>
    delivery.siteName ?? delivery.organizationName ?? delivery.totem?.name ?? delivery._id;

/**
 * Detail label (hover cards, map popups). A freefood delivery (no site) is named like on its delivery note —
 * « Organisation #20 (totem) » — since one organisation may have several totems and the chips only show the organisation.
 */
export const deliveryFullLabel = (delivery: TourDelivery): string =>
    !delivery.siteName && delivery.totem
        ? getDeliveryNoteTotemLabel({
              organizationName: delivery.organizationName,
              number: delivery.totem.number,
              name: delivery.totem.name?.trim(),
          })
        : deliveryLabel(delivery);

/** Where to draw the delivery — the totem carries the address, whether it is a site or a freefood order. */
export const stopCoordinates = (stop: Pick<TourStopItem, 'totem'>): { latitude: number; longitude: number } | null => {
    const { latitude, longitude } = stop.totem ?? {};
    return latitude != null && longitude != null ? { latitude, longitude } : null;
};

/** Delivery/reception window of the day, `HH:mm` — the site's, else the freefood order's. */
export const deliveryWindow = (delivery: TourDelivery): { start: string | null; stop: string | null } => {
    const window = delivery.site?.deliveryTimeWindow ?? delivery.orders[0]?.deliveryTimeWindows[0];
    return { start: window?.start || null, stop: window?.stop || null };
};

export const deliveryZoneName = (delivery: TourDelivery): string | null => delivery.site?.deliveryZone?.name ?? null;

export const deliveryContractLabel = (delivery: TourDelivery): string | null =>
    delivery.site?.contractType ? SITE_CONTRACT_TYPES_OPTIONS[delivery.site.contractType].label : null;

export const stopAddress = (stop: Pick<TourStopItem, 'totem'>): string | null => {
    const { address, zipcode } = stop.totem ?? {};
    if (!address) return null;
    if (!zipcode || address.includes(zipcode)) return address;

    return `${address}, ${zipcode}`;
};

export const deliveryAddressLabel = (delivery: TourDelivery): string => stopAddress(delivery) ?? 'Adresse inconnue';

/** The delivery card's muted line: address, zone and contract (when they exist), columns to serve — one string, also the hover title. */
export const deliveryMetaLabel = (delivery: TourDelivery): string =>
    [
        deliveryAddressLabel(delivery),
        deliveryZoneName(delivery),
        deliveryContractLabel(delivery),
        delivery.deliveredMicrostoreColumnIds.length > 0 && `${delivery.deliveredMicrostoreColumnIds.length} col.`,
    ]
        .filter(Boolean)
        .join(' · ');

export type ColumnCounts = { fresh: number; hotDrinks: number; others: number };

/** Template type of every column served (a column without a microstore column — virtual stock — has none). */
export const deliveryColumnTypes = (delivery: TourDelivery): MicrostoreColumnTemplateType[] =>
    delivery.deliveredMicrostoreColumns.flatMap(({ microstoreColumn }) =>
        microstoreColumn ? [microstoreColumn.type] : [],
    );

/** Columns served, by machine family: fridges, hot drinks, everything else (snacks, drinks, essentials…). */
export const deliveryColumnCounts = (delivery: TourDelivery): ColumnCounts => {
    const types = deliveryColumnTypes(delivery);
    const fresh = types.filter((type) => type === MicrostoreColumnTemplateType.Fridge).length;
    const hotDrinks = types.filter((type) => type === MicrostoreColumnTemplateType.HotDrinks).length;

    return { fresh, hotDrinks, others: types.length - fresh - hotDrinks };
};

/** Freefood on board = an order rides in this delivery — the API's own rule (its boxes carry an `orderId`). */
export const hasFreefood = (delivery: TourDelivery): boolean => delivery.orderIds.length > 0;

/** « 2 frais · 1 boissons chaudes · 3 autres · freefood » — what the task carries, in its hover card. */
export const deliveryContentSummary = (delivery: TourDelivery): string => {
    const { fresh, hotDrinks, others } = deliveryColumnCounts(delivery);
    return [
        fresh > 0 && `${fresh} frais`,
        hotDrinks > 0 && `${hotDrinks} boissons chaudes`,
        others > 0 && `${others} autres`,
        hasFreefood(delivery) && 'freefood',
    ]
        .filter(Boolean)
        .join(' · ');
};

/** Store or freefood by delivery type — the API's own split (MICROSTORE_DELIVERY_TYPES / ORDER_DELIVERY_TYPES). */
export const DESTINATION_BY_DELIVERY_TYPE: Partial<Record<DeliveryType, DeliveryDestinationType>> = {
    [DeliveryType.StockTransfer]: DeliveryDestinationType.Store,
    [DeliveryType.PackageDelivery]: DeliveryDestinationType.Store,
    [DeliveryType.Order]: DeliveryDestinationType.Freefood,
    [DeliveryType.Punctual]: DeliveryDestinationType.Freefood,
};

export const deliveryHref = (deliveryId: string): string => `/delivery/${deliveryId}`;

export const tourHref = (tourId: string): string => `/tour/${tourId}`;

const findTask = (
    tours: Tour[],
    unassignedTasks: TourTask[],
    taskId: string,
): { task: TourTask; from: Tour | null } | null => {
    for (const tour of tours) {
        const stop = tour.stops.find((candidate) => candidate._id === taskId);
        if (stop?.__typename === 'Task') return { task: stop, from: tour };
    }
    const loose = unassignedTasks.find((candidate) => candidate._id === taskId);
    return loose ? { task: loose, from: null } : null;
};

export const stopLabel = (stop: TourStopItem): string => (isTask(stop) ? taskLabel(stop) : deliveryLabel(stop));

export const stopWindow = (stop: TourStopItem): { start: string | null; stop: string | null } =>
    isTask(stop)
        ? { start: stop.site?.deliveryTimeWindow.start || null, stop: stop.site?.deliveryTimeWindow.stop || null }
        : deliveryWindow(stop);

/** Resolves a dragged deliveryId to its delivery and source tour (null when it comes from « à affecter »). */
const findDelivery = (
    tours: Tour[],
    unassignedDeliveries: TourDelivery[],
    deliveryId: string,
): { delivery: TourDelivery; from: Tour | null } | null => {
    for (const tour of tours) {
        const delivery = tour.deliveries.find((candidate) => candidate._id === deliveryId);
        if (delivery) return { delivery, from: tour };
    }
    const loose = unassignedDeliveries.find((candidate) => candidate._id === deliveryId);
    return loose ? { delivery: loose, from: null } : null;
};

export type FoundStop = { stop: TourStopInput; label: string; from: Tour | null };

export const findStop = (
    tours: Tour[],
    unassignedDeliveries: TourDelivery[],
    unassignedTasks: TourTask[],
    stopId: string,
): FoundStop | null => {
    const found = findDelivery(tours, unassignedDeliveries, stopId);
    if (found) {
        return {
            stop: { type: TourStopType.Delivery, stopId },
            label: deliveryLabel(found.delivery),
            from: found.from,
        };
    }
    const foundTask = findTask(tours, unassignedTasks, stopId);
    return foundTask
        ? { stop: { type: TourStopType.Task, stopId }, label: taskLabel(foundTask.task), from: foundTask.from }
        : null;
};

/** "7:45" / "07:45" → 465. Null/empty/malformed → null. */
export const parseTimeToMinutes = (value: string | null | undefined): number | null => {
    if (!value) return null;
    const [hoursPart, minutesPart] = value.split(':');
    const hours = Number(hoursPart);
    const minutes = Number(minutesPart ?? 0);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return hours * 60 + minutes;
};

/** minutes → "HH:mm". Null → "—". */
export const formatMinutes = (minutes: number | null): string => {
    if (minutes == null) return '—';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

export const formatWindow = (start: string | null | undefined, stop: string | null | undefined): string => {
    if (!start && !stop) return 'Horaire libre';
    return `${formatMinutes(parseTimeToMinutes(start))}–${formatMinutes(parseTimeToMinutes(stop))}`;
};

// Tour workload = driving time + on-site service time. The service time is the delivery's own
// `theoricServiceTime`, computed by the API (Site.accessTime + the Ops serviceTimePerBox + the day's
// rangement) — the one ops watches for drift. Only the travel leg is
// ours: the API has no route.
/** Flat inter-delivery travel time, used wherever no real IGN route is available (timeline, appro-day load). */
export const TRAVEL_MINUTES = 15;

/** Road time after a stop: none when the next stop is at the same place (same site, or same address). */
export const travelMinutesAfter = (stops: TourStopItem[], index: number): number => {
    const here = stops[index]?.totem;
    const next = stops[index + 1]?.totem;
    const isSamePlace =
        here?.latitude != null &&
        here.longitude != null &&
        here.latitude === next?.latitude &&
        here.longitude === next.longitude;
    return isSamePlace ? 0 : TRAVEL_MINUTES;
};
/** A delivery person's day ceiling — beyond it a tour, or an appro's day, is flagged as overloaded. */
export const MAX_TOUR_MINUTES = 7 * 60;
/** Gennevilliers warehouse — the tours' start and end. Same depot the API keys its dry columns on. */
export const GENNEVILLIERS_DEPOT = {
    name: 'Entrepôt Gennevilliers',
    address: '4 route du Bassin N°1, 92230 Gennevilliers — Bâtiment A3 / Hall 2 et 3',
    latitude: 48.937084,
    longitude: 2.268629,
};
/** Time at the sas before leaving: the boxes scan. */
export const DEPOT_SAS_MINUTES = 15;
/** Depot ↔ first/last delivery on the road when the IGN route is not available (no coordinates, fetch failed). */
export const DEPOT_ROAD_FALLBACK_MINUTES = 30;

/** What the page's estimate is made of — tooltip on the overload KPI. */
export const TOUR_LOAD_HINT = `Charge = temps de service de chaque arrêt (livraison : calculé par l'API, accès au site + temps par caisse + rangement du jour ; tâche : temps sur place de son type) + ${TRAVEL_MINUTES} min de trajet par arrêt, sauf entre deux arrêts du même site — alerte au-delà de ${MAX_TOUR_MINUTES / 60} h par appro et par jour`;

/** Earliest window start / latest window stop (minutes) across a set of stops; null when none configured. */
export const windowBounds = (
    stops: TourStopItem[],
): { windowStartMin: number | null; windowStopMin: number | null } => {
    const starts = stops
        .map((stop) => parseTimeToMinutes(stopWindow(stop).start))
        .filter((value): value is number => value != null);
    const ends = stops
        .map((stop) => parseTimeToMinutes(stopWindow(stop).stop))
        .filter((value): value is number => value != null);
    return {
        windowStartMin: starts.length ? Math.min(...starts) : null,
        windowStopMin: ends.length ? Math.max(...ends) : null,
    };
};

export type TourOrigin = 'fixe' | 'dynamique' | 'mixte';

export const tourOrigin = (tour: Pick<Tour, 'deliveries'>): TourOrigin => {
    const dynamicCount = tour.deliveries.filter((delivery) => delivery.isDynamic).length;
    if (dynamicCount === 0) return 'fixe';
    if (dynamicCount === tour.deliveries.length) return 'dynamique';
    return 'mixte';
};

export type TourStatut = 'Créée' | 'Prête' | 'En cours' | 'Terminée' | 'Archivée';

export const STATUT_COLOR: Record<TourStatut, string> = {
    Créée: '#64748b',
    Prête: '#0f766e',
    'En cours': '#2563eb',
    Terminée: '#16a34a',
    Archivée: '#94a3b8',
};

export const UNASSIGNED_STATUT_COLOR = '#d97706';

// `finish-all-deliveries` (19h, Mon–Fri) forces every stop of the day still open to `Delivered`, the not delivered
// ones included: `systems/api/kube/cronjobs/base/finish-all-deliveries.yml`.
const FORCE_FINISH_CRON = 'CRON finish-all-deliveries';

/**
 * The state a stop is shown with, read back from its history where an end-of-day cron rewrote it:
 * - a stop closed as not delivered stays « Non livrée » once `finish-all-deliveries` forced it to delivered;
 * - `Archived` is the 20h cron's, not the stop's story — it archives the delivered and the cancelled stops alike — so
 *   an archived stop shows what it was: cancelled if it ever was, delivered otherwise.
 */
export const getStopState = (delivery: Pick<TourDelivery, 'state' | 'stateHistory'>): DeliveryState => {
    const setStates = delivery.stateHistory.filter(
        ({ state, createdBy }) => state !== DeliveryState.Archived && createdBy !== FORCE_FINISH_CRON,
    );
    if (setStates[setStates.length - 1]?.state === DeliveryState.NotDelivered) return DeliveryState.NotDelivered;
    if (delivery.state !== DeliveryState.Archived) return delivery.state;
    return delivery.stateHistory.some(({ state }) => state === DeliveryState.Aborted)
        ? DeliveryState.Aborted
        : DeliveryState.Delivered;
};

/** The tour's boxes, all deliveries together: what left the warehouse (`loadedAt`) and what came back (`returnedAt`). */
export const boxCounts = (tour: Pick<Tour, 'deliveries'>): { total: number; loaded: number; returned: number } => {
    const boxes = tour.deliveries.flatMap((delivery) => delivery.boxes);
    return {
        total: boxes.length,
        loaded: boxes.filter((box) => box.loadedAt != null).length,
        returned: boxes.filter((box) => box.returnedAt != null).length,
    };
};

/** Columns to serve on the whole tour — every stop's « Colonnes » added up. */
export const tourColumnCount = (tour: Pick<Tour, 'deliveries'>): number =>
    tour.deliveries.reduce((sum, delivery) => sum + delivery.deliveredMicrostoreColumnIds.length, 0);

/** « 12 Sec · 5 Frais » — the tour's boxes per flux, in the bundle types' order, empty flux left out. */
export const boxesByFlowLabel = (tour: Pick<Tour, 'deliveries'>): string => {
    const boxes = tour.deliveries.flatMap((delivery) => delivery.boxes);
    return Object.entries(BUNDLE_TYPES_LABELS)
        .map(([type, label]) => ({ label, count: boxes.filter((box) => box.type === type).length }))
        .filter(({ count }) => count > 0)
        .map(({ label, count }) => `${count} ${label}`)
        .join(' · ');
};

/** The sas check has started: at least one box was scanned back. Before that, a null `returnedAt` is pending. */
export const hasBoxReturnStarted = (tour: Pick<Tour, 'deliveries'>): boolean => boxCounts(tour).returned > 0;

/**
 * The tour's driving lifecycle label. `Prête` is the warehouse manager's departure check — every box of the
 * tour scanned out of the sas. The box return is not a status: it is read off the boxes on the tour's page.
 */
export const tourStateLabel = (state: TourState): TourStatut => {
    switch (state) {
        case TourState.Ready:
            return 'Prête';
        case TourState.InDelivery:
            return 'En cours';
        case TourState.Finished:
            return 'Terminée';
        case TourState.Archived:
            return 'Archivée';
        default:
            return 'Créée';
    }
};

/** The states as select options, in workflow order — the generated enum is alphabetical. */
export const tourStateOptions = (): { value: TourState; label: TourStatut }[] =>
    [TourState.Created, TourState.Ready, TourState.InDelivery, TourState.Finished, TourState.Archived].map((state) => ({
        value: state,
        label: tourStateLabel(state),
    }));

/** Stops the appro could not deliver. `Aborted` is an ops cancellation, not a failure — excluded. */
export const failedDeliveryCount = (tour: Pick<Tour, 'deliveries'>): number =>
    tour.deliveries.filter((delivery) => getStopState(delivery) === DeliveryState.NotDelivered).length;

export type DeliveryPersonLoad = { tours: number; deliveries: number; minutes: number; isOverloaded: boolean };

/**
 * Workload per APPRO-DAY, not per tour: an appro driving two tours does one 10-hour day, and no per-tour
 * total would ever show it. This is what flags a dispatcher's overload before the tours are merged.
 */
export const deliveryPersonDayLoads = (tours: Tour[]): Map<string, DeliveryPersonLoad> => {
    const loads = new Map<string, DeliveryPersonLoad>();

    for (const tour of tours) {
        if (!tour.deliveryPersonUserId) continue;
        const minutes = orderedStops(tour).reduce(
            (sum, stop, index, stops) => sum + (stop.theoricServiceTime ?? 0) + travelMinutesAfter(stops, index),
            0,
        );
        const current = loads.get(tour.deliveryPersonUserId) ?? {
            tours: 0,
            deliveries: 0,
            minutes: 0,
            isOverloaded: false,
        };
        const updated = {
            tours: current.tours + 1,
            deliveries: current.deliveries + tour.deliveries.length,
            minutes: current.minutes + minutes,
        };
        loads.set(tour.deliveryPersonUserId, { ...updated, isOverloaded: updated.minutes > MAX_TOUR_MINUTES });
    }

    return loads;
};
